import { getCurrentShowOddsValue, getCurrentShrkValue, getCurrentUserTierValue } from "./FetchStats.js";
import FetchTopPlaysStatic from "./FetchTopPlaysStatic.js";
import { normalizeProjectedStatKey } from "./selectionState.js";
import reportBoardIssue from "./ReportBoardIssue.js";
import restFetchOptions, { getCurrentRestViewer, recoverInvalidRestNonceResponse } from "./restFetchOptions.js";
import { createBoardRequestId, recordBoardRequestTelemetry } from "./BoardRequestTelemetry.js";
const { settleBoardRequestWithin } = require("./boardRequestGuards.js");

export default async function FetchAthletes(sport, stat = false, gameId = false, options = {}) {
	// Cold Popular/Top Picks builds can legitimately take a little over five
	// seconds. Keep a hard WebKit-safe deadline, but leave enough headroom for
	// that first build to complete before the 15-second UI recovery guard runs.
	const FETCH_TIMEOUT_MS = 10000;
	const TRANSIENT_HTTP_RETRY_DELAYS_MS = [400, 1200];
	// A gateway timeout can leave the original PHP request running. Retrying it
	// immediately amplifies worker pressure and cannot safely improve latency.
	const TRANSIENT_HTTP_STATUSES = new Set([429, 500, 502, 503]);
	const SLOW_LOG_THRESHOLD_MS = 3500;   // log successful-but-slow calls to help debugging
	const ENABLE_FETCH_DEBUG = false;     // flip to true when we need client-side logging again
	const externalSignal = options?.signal;
	const forceFresh = options?.forceFresh === true;
	const shrk = (options?.shrk ?? getCurrentShrkValue()).toString();
	const showOdds = (options?.showOdds ?? getCurrentShowOddsValue()).toString();
	const requestedLimit = Number.isFinite(Number(options?.limit)) && Number(options.limit) > 0
		? Math.floor(Number(options.limit))
		: 0;
	const requestedOffset = Number.isFinite(Number(options?.offset)) && Number(options.offset) > 0
		? Math.floor(Number(options.offset))
		: 0;
	const canUseTopPlaysStatic =
		String(sport || "").toLowerCase() === "topplays" &&
		normalizeProjectedStatKey(stat || "") === "projected_popular" &&
		!gameId &&
		requestedOffset === 0 &&
		shrk !== "1" &&
		showOdds !== "1" &&
		Number(getCurrentUserTierValue()) <= 1;

	if (canUseTopPlaysStatic) {
		const staticRows = await FetchTopPlaysStatic();
		if (externalSignal?.aborted) {
			return { aborted: true, data: [] };
		}
		if (Array.isArray(staticRows) && staticRows.length > 0) {
			// Top Picks returns the complete three-league Popular rail even when
			// the board bootstrap asks for its visible limit.
			return staticRows;
		}
	}

  // Keep current URL handy for debugging context (not used for routing right now)
  const currentURL = window.location.href;
  const encodePathSegment = (value) =>
    encodeURIComponent(String(value)).replace(/%[0-9A-F]{2}/g, (m) => m.toLowerCase());

  const safeSport = encodePathSegment(sport);
  const normalizedStat = stat ? normalizeProjectedStatKey(stat) : "";
  // Keep "+" literal in stat keys (e.g., projected_pts+asts) to avoid redirect/canonical rewrites.
  const safeStat = normalizedStat ? encodePathSegment(normalizedStat).replace(/%2b/g, "+") : "";

  const route = normalizedStat ? `athletes/v1/${safeSport}/${safeStat}` : `athletes/v1/${safeSport}`;

  let url = `/wp-json/${route}`; // Use relative URL
  const viewerSep = url.includes("?") ? "&" : "?";
  url = `${url}${viewerSep}viewer=${encodeURIComponent(getCurrentRestViewer())}`;
  if (gameId) {
    // append game_id as query param
    const sep = url.includes("?") ? "&" : "?";
    url = `${url}${sep}game_id=${encodeURIComponent(gameId)}`;
  }
	if (requestedLimit > 0) {
		const sep = url.includes("?") ? "&" : "?";
		url = `${url}${sep}limit=${encodeURIComponent(requestedLimit)}`;
	}
	if (requestedOffset > 0) {
		const sep = url.includes("?") ? "&" : "?";
		url = `${url}${sep}offset=${encodeURIComponent(requestedOffset)}`;
	}
	if (shrk !== "") {
		const sep = url.includes("?") ? "&" : "?";
		url = `${url}${sep}shrk=${encodeURIComponent(shrk)}`;
	}
	if (showOdds === "1") {
		const sep = url.includes("?") ? "&" : "?";
		url = `${url}${sep}show_odds=1`;
	}
	  const sep = url.includes("?") ? "&" : "?";
	  url = `${url}${sep}fast=1`;
	  if (forceFresh) {
	    // A cache clear removes WordPress result caches, but an intermediary may
	    // still hold the prior empty response for its public-cache lifetime.
	    url = `${url}&ww_fresh=${encodeURIComponent(Date.now())}`;
	  }
	  if (typeof window !== "undefined") {
	    window.__WW_LAST_ATHLETES_FETCH_URL = url;
	  }

  const getNow = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());
	const waitForRetry = (delayMs) => new Promise((resolve) => {
		const timer = window.setTimeout(resolve, delayMs);
		if (externalSignal) {
			externalSignal.addEventListener("abort", () => {
				window.clearTimeout(timer);
				resolve();
			}, { once: true });
		}
	});

  const logDebug = (stage, details = {}) => {
    if (!ENABLE_FETCH_DEBUG) return;
    const conn = navigator?.connection;
    const payload = {
      stage,
      sport,
      stat,
      gameId,
      url,
      page: currentURL,
      host: window.location.host,
      path: window.location.pathname,
      ts: new Date().toISOString(),
      connection: conn
        ? {
            effectiveType: conn.effectiveType,
            downlink: conn.downlink,
            rtt: conn.rtt,
          }
        : null,
      ...details,
    };

    try {
      window.__WW_FETCH_DEBUG = window.__WW_FETCH_DEBUG || [];
      window.__WW_FETCH_DEBUG.push(payload);
      // keep last 10 entries to avoid unbounded growth
      window.__WW_FETCH_DEBUG = window.__WW_FETCH_DEBUG.slice(-10);
      localStorage.setItem("ww_fetch_debug", JSON.stringify(window.__WW_FETCH_DEBUG));
    } catch (e) {
      // Swallow storage errors; logging should never break fetch.
    }

    // Visible in the browser console for quick triage.
    console.warn("[FetchAthletes]", payload);
  };

  const fetchWithTimeout = async (timeoutMs, attemptLabel) => {
    const controller = new AbortController();
    const startedAt = getNow();
    const requestId = createBoardRequestId();

    try {
      const fetchOptions = restFetchOptions({
        signal: controller.signal,
        headers: { "X-WW-Request-Id": requestId },
        ...(forceFresh ? { cache: "reload" } : {}),
      });
      const request = (async () => {
        let response;
        try {
          response = await fetch(url, fetchOptions);
        } catch (error) {
          if (error.name === "AbortError" || controller.signal.aborted) {
            throw error;
          }
          response = await fetch(url, fetchOptions);
        }
        response = await recoverInvalidRestNonceResponse(response, url, fetchOptions);

        if (!response.ok) {
          const responseRequestId = response.headers.get("cf-ray")
            || response.headers.get("x-request-id")
            || response.headers.get("x-amzn-trace-id")
            || "";
          const responseMessage = await response.clone().text()
            .then((body) => body.replace(/\s+/g, " ").trim().slice(0, 240))
            .catch(() => "");
          return {
            kind: "http-error",
            status: response.status,
            message: [responseRequestId ? `request-id:${responseRequestId}` : "", responseMessage].filter(Boolean).join(" "),
          };
        }

		recordBoardRequestTelemetry({ requestId, httpStatus: response.status, outcome: "success", route });

        return {
          kind: "success",
          status: response.status,
          data: await response.json(),
        };
      })();
      const deadlineResult = await settleBoardRequestWithin(request, {
        timeoutMs,
        signal: externalSignal,
        onTimeout: () => controller.abort(),
        onAbort: () => controller.abort(),
        timeoutValue: { boardDeadline: "timeout" },
        abortedValue: { boardDeadline: "aborted" },
      });
      const durationMs = Math.round(getNow() - startedAt);

      if (deadlineResult?.boardDeadline === "aborted") {
		recordBoardRequestTelemetry({ requestId, httpStatus: 0, outcome: "aborted", route });
        return { ok: false, aborted: true, durationMs, attemptLabel };
      }
      if (deadlineResult?.boardDeadline === "timeout") {
		recordBoardRequestTelemetry({ requestId, httpStatus: 0, outcome: "timeout", route });
        return { ok: false, timedOut: true, durationMs, attemptLabel };
      }

      if (deadlineResult?.kind === "http-error") {
		recordBoardRequestTelemetry({ requestId, httpStatus: deadlineResult.status, outcome: "http-error", route });
        return {
          ok: false,
          status: deadlineResult.status,
          durationMs,
          attemptLabel,
          message: deadlineResult.message || "",
        };
      }

      const data = deadlineResult?.data;
      if (!Array.isArray(data) && !Array.isArray(data?.data)) {
        void reportBoardIssue("athletes-broken-payload", {
          sport,
          stat: normalizedStat || stat || "",
          gameId,
          url,
          status: deadlineResult?.status,
          durationMs,
          requestedLimit,
          requestedOffset,
          responseKind: Array.isArray(data) ? "array" : typeof data,
        });
      }
      return { ok: true, data, durationMs, attemptLabel };
    } catch (error) {
      const durationMs = Math.round(getNow() - startedAt);

      if (error.name === "AbortError") {
        if (externalSignal?.aborted) {
		  recordBoardRequestTelemetry({ requestId, httpStatus: 0, outcome: "aborted", route });
          return { ok: false, aborted: true, durationMs, attemptLabel };
        }
		recordBoardRequestTelemetry({ requestId, httpStatus: 0, outcome: "timeout", route });
        return { ok: false, timedOut: true, durationMs, attemptLabel };
      }

	  recordBoardRequestTelemetry({ requestId, httpStatus: 0, outcome: "network-error", route });
      return { ok: false, error, durationMs, attemptLabel };
    }
  };

  // Retry only responses that are safe to retry. A short database or upstream
  // interruption must not immediately turn a valid market into an empty board.
  let firstAttempt = await fetchWithTimeout(FETCH_TIMEOUT_MS, "primary");
	for (let retryIndex = 0; retryIndex < TRANSIENT_HTTP_RETRY_DELAYS_MS.length; retryIndex += 1) {
		if (
			firstAttempt.ok ||
			firstAttempt.aborted ||
			firstAttempt.timedOut ||
			firstAttempt.error ||
			!TRANSIENT_HTTP_STATUSES.has(Number(firstAttempt.status))
		) {
			break;
		}

		await waitForRetry(TRANSIENT_HTTP_RETRY_DELAYS_MS[retryIndex]);
		if (externalSignal?.aborted) {
			return { aborted: true, data: [] };
		}
		firstAttempt = await fetchWithTimeout(FETCH_TIMEOUT_MS, `transient-http-${retryIndex + 1}`);
	}

  if (firstAttempt.ok) {
    if (firstAttempt.durationMs >= SLOW_LOG_THRESHOLD_MS) {
      logDebug("slow-success", { durationMs: firstAttempt.durationMs, timeoutMs: FETCH_TIMEOUT_MS });
    }
    return firstAttempt.data;
  }

  if (firstAttempt.aborted) {
    return { aborted: true, data: [] };
  }

  if (firstAttempt.timedOut) {
    logDebug("timeout-primary", { durationMs: firstAttempt.durationMs, timeoutMs: FETCH_TIMEOUT_MS });
    return { timeout: true, data: [] };
  }

  if (firstAttempt.error) {
    logDebug("fetch-error", { message: firstAttempt.error.message, durationMs: firstAttempt.durationMs });
    return { error: true, data: [] };
  }

  // HTTP error that is not a timeout
  logDebug("http-error", { status: firstAttempt.status, durationMs: firstAttempt.durationMs });
  void reportBoardIssue("athletes-http-error", {
    sport,
    stat: normalizedStat || stat || "",
    gameId,
    url,
    status: firstAttempt.status,
    durationMs: firstAttempt.durationMs,
    requestedLimit,
    requestedOffset,
	message: firstAttempt.message || "",
  });
  return { error: true, data: [] };
}
